/**
 * Feb 2026 · Pending Statement Report.
 *
 * Printable / PDF-friendly rollup of every unshipped SO + ESO line. Three
 * sections composed from a single `/api/reports/pending-statement` call:
 *   1. SO — Line-wise pending (per SO, per article)
 *   2. ESO — Line-wise pending (per Export SO)
 *   3. Article / Product-Code-wise aggregate across BOTH SO + ESO
 *
 * The Print button uses `window.print()` with print-friendly CSS in
 * App.css → `.print-hide` (nav/filters hidden), `.print-only` (page header
 * shows only in print).
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  PrinterIcon, DownloadSimpleIcon, WarningCircleIcon,
  ArrowsClockwiseIcon, MagnifyingGlassIcon, StackIcon, XIcon,
  CaretDownIcon, FilePdfIcon,
} from "@phosphor-icons/react";
import { exportToCSV } from "@/lib/csv";
import TwoLineDocNo from "@/components/TwoLineDocNo";
import { COMPANY, getPrintCompany } from "@/config/company";

const fmt = (n, d = 2) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: d, minimumFractionDigits: d });
const fmtQty = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
const fmtOpt = (n) => (n === null || n === undefined ? "—" : fmtQty(n));

// PSOR-01 · seven-column quantity breakdown (presentation of backend-authoritative fields).
const QTY_COLS = [
  { k: "original_qty", label: "Original Qty" },
  { k: "add_qty", label: "Add Qty" },
  { k: "effective_qty", label: "Effective Qty" },
  { k: "cancel_qty", label: "Cancel Qty" },
  { k: "close_qty", label: "Close Qty" },
  { k: "disp_qty", label: "Delivered Qty" },
  { k: "pending_qty", label: "Pending Qty" },
];

export function sumQtyCols(rows) {
  const t = { value: 0, partial: false, partialCols: new Set() };
  QTY_COLS.forEach(c => { t[c.k] = 0; });
  for (const r of rows) {
    for (const c of QTY_COLS) {
      if (r[c.k] === null || r[c.k] === undefined) { t.partial = true; t.partialCols.add(c.k); }
      else t[c.k] += Number(r[c.k] || 0);
    }
    t.value += Number(r.pending_value || 0);
  }
  return t;
}

// PSOR-01 G7/G9 · product aggregate rebuilt from the SAME visible lines (backend product_group_key).
export function aggregateProducts(lines) {
  const acc = {};
  for (const r of lines) {
    const key = r.product_group_key || "—";
    const e = acc[key] || {
      product_group_key: key, article_no: (r.article_no || "").trim() || "—", description: r.description || "",
      pending_qty: 0, pending_value: 0, order_count: 0, customers: new Set(), orders: new Set(),
      earliest_delivery_date: "", product_conflict: !!r.product_conflict,
    };
    e.pending_qty += Number(r.pending_qty || 0);
    e.pending_value += Number(r.pending_value || 0);
    e.order_count += 1;
    if (r.customer_name) e.customers.add(r.customer_name);
    e.orders.add(`${r.source} · ${r.order_no}`);
    const d = r.delivery_date || "";
    if (d && (!e.earliest_delivery_date || d < e.earliest_delivery_date)) e.earliest_delivery_date = d;
    acc[key] = e;
  }
  return Object.values(acc)
    .map(e => ({ ...e, customers: [...e.customers].sort(), orders: [...e.orders].sort() }))
    .sort((a, b) => (a.earliest_delivery_date || "~").localeCompare(b.earliest_delivery_date || "~") || b.pending_qty - a.pending_qty);
}

export function productSumTotal(rows) {
  return rows.reduce((a, r) => ({
    pending_qty: a.pending_qty + Number(r.pending_qty || 0),
    pending_value: a.pending_value + Number(r.pending_value || 0),
    order_lines: a.order_lines + Number(r.order_count || 0),
  }), { pending_qty: 0, pending_value: 0, order_lines: 0 });
}

function CancelCell({ r, print }) {
  if (r.cancel_close_split_available !== false) return fmtOpt(r.cancel_qty);
  return (
    <span data-testid="ps-cancel-close-note">—
      <div className={print ? "text-[7px] text-gray-600" : "text-[9px] text-zinc-500"}>Legacy Cancelled/Closed — split unavailable: {fmtQty(r.combined_cancel_close_qty)}</div>
    </span>
  );
}

// R2-01L A4-02 · G13 · existing server protection status — display only (quantities unchanged).
function ProtectionFlag({ r, print }) {
  if (!r.readiness_block) return null;
  return (
    <span data-testid="ps-protection-flag" title="Quantity changes are blocked for this line"
      className={print ? "ml-1 text-[8px] font-bold" : "ml-1 text-[9px] font-bold text-amber-300 uppercase"}>[{r.readiness_block.replace("_", " ")}]</span>
  );
}

function ConflictFlag({ print }) {
  return (
    <span data-testid="ps-product-conflict" title="Same code (ignoring case/spaces) maps to different Product Master records — kept separate"
      className={print ? "ml-1 text-[8px] font-bold" : "ml-1 text-[9px] font-bold text-red-400 uppercase"}>[Conflict]</span>
  );
}

const PARTIAL_NOTE = "† Excludes historical lines without a verified Original / Add / Cancel / Close baseline (shown as —).";
const TIER_C_NOTE = "‡ Original Qty corroborated from historical records (Evidence Tier C — authorisation-time quantity not directly recorded; see baseline evidence). Not equivalent to a recorded baseline.";
const tierMark = (r, k) => (k === "original_qty" && r.original_qty_tier === "C" ? " ‡" : "");
const hasTierC = (rows) => rows.some(r => r.original_qty_tier === "C");

// Feb 2026 · Delivery-status colour helper — red when overdue, amber when
// due in ≤7 days, muted otherwise. Powers both the on-screen line rows
// and the print preview so the same signal shows on paper.
const _todayIso = () => new Date().toISOString().slice(0, 10);
function deliveryStatus(dateStr) {
  if (!dateStr) return { label: "—", color: "text-zinc-600", print: "" };
  const today = _todayIso();
  const days = Math.round((new Date(dateStr) - new Date(today)) / 86400000);
  if (days < 0) return { label: `${-days}d overdue`, color: "text-red-400 font-bold", print: "text-red-700" };
  if (days <= 7) return { label: `${days}d left`, color: "text-amber-400 font-semibold", print: "text-amber-700" };
  return { label: `${days}d`, color: "text-zinc-400", print: "" };
}

export default function PendingStatement() {
  const [data, setData] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("so");   // 'so' | 'eso' | 'article'
  const [riskFilter, setRiskFilter] = useState("all");
  const [previewOpen, setPreviewOpen] = useState(false);
  // Feb 2026 · Customer Filter — scopes SO + ESO + article roll-up to a
  // single customer so account managers can email one PDF per client.
  const [customerName, setCustomerName] = useState("");
  const [customerPickerOpen, setCustomerPickerOpen] = useState(false);
  const [customerSearch, setCustomerSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [rep, cust] = await Promise.all([
        api.get("/reports/pending-statement"),
        api.get("/customers/").catch(() => ({ data: [] })),
      ]);
      setData(rep.data);
      setCustomers(cust.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load pending statement");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const co = useMemo(() => getPrintCompany() || COMPANY, []);

  // Only customers actually present on open lines — no dead dropdown entries.
  const customerOptions = useMemo(() => {
    if (!data) return [];
    const names = new Set();
    for (const l of (data.so_lines || [])) if (l.customer_name) names.add(l.customer_name);
    for (const l of (data.eso_lines || [])) if (l.customer_name) names.add(l.customer_name);
    return [...names].sort();
  }, [data]);

  // Master lookup for the chosen name so the PDF header can render the
  // billing address / GSTIN / contact block. ESO buyers may not be in
  // `customers` — falls back to a name-only stub in that case.
  const selectedCustomer = useMemo(() => {
    if (!customerName) return null;
    const master = customers.find(c => c.name === customerName);
    if (master) {
      const bill = (master.addresses || []).find(a => (a.label || "").toLowerCase().includes("bill")) || (master.addresses || [])[0] || {};
      return {
        name: master.name,
        email: master.email || bill.email || "",
        phone: master.phone || bill.contact_phone || bill.phone || "",
        contact_person: master.contact_person || bill.contact_person || "",
        address: bill.address || "",
        city: bill.city || "", state: bill.state || "", pincode: bill.pincode || "", country: bill.country || "",
        gstin: bill.gstin || "",
      };
    }
    return { name: customerName };
  }, [customerName, customers]);

  const filtered = useMemo(() => {
    if (!data) return { so_lines: [], eso_lines: [], article_rows: [], product_total: productSumTotal([]) };
    const needle = q.trim().toLowerCase();
    const today = _todayIso();
    const day = 86400000;
    const daysTo = (row) => {
      const d = row.delivery_date || row.earliest_delivery_date || "";
      if (!d) return Number.POSITIVE_INFINITY;
      return Math.round((new Date(d) - new Date(today)) / day);
    };
    const passRisk = (row) => {
      if (riskFilter === "all") return true;
      const dt = daysTo(row);
      if (riskFilter === "overdue") return dt < 0;
      if (riskFilter === "due7") return dt <= 7;
      if (riskFilter === "due30") return dt <= 30;
      return true;
    };
    const passCustomer = (r) => !customerName || r.customer_name === customerName;
    const match = (r) => passCustomer(r) && passRisk(r) && (!needle || Object.values(r).some(v => String(v ?? "").toLowerCase().includes(needle)));
    const so_lines  = (data.so_lines || []).filter(match);
    const eso_lines = (data.eso_lines || []).filter(match);
    const article_rows = aggregateProducts([...so_lines, ...eso_lines]);
    return { so_lines, eso_lines, article_rows, product_total: productSumTotal(article_rows) };
  }, [data, q, riskFilter, customerName]);

  const doPrint = () => {
    const sheet = document.getElementById("ps-print-sheet");
    if (!sheet) { window.print(); return; }
    const styles = Array.from(document.querySelectorAll('link[rel="stylesheet"], style'))
      .map(node => node.outerHTML).join("\n");
    document.getElementById("__ps_print_iframe")?.remove();
    const iframe = document.createElement("iframe");
    iframe.id = "__ps_print_iframe";
    iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;";
    document.body.appendChild(iframe);
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Pending Sales Order</title>
      <style>
        @page { size: A4 landscape; margin: 8mm; }
        @page { size: 297mm 210mm; margin: 8mm; }
        html, body {
          background: #fff !important; color: #000 !important;
          margin: 0 !important; padding: 0 !important;
          width: 297mm; overflow: visible;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
      </style>
      ${styles}
      <style>
        #ps-print-sheet { position: static !important; width: 100% !important; min-height: auto !important; box-shadow: none !important; padding: 0 !important; background: #fff !important; color: #000 !important; }
        table { color: #000 !important; page-break-inside: auto; }
        table thead { background: #eee !important; color: #000 !important; display: table-header-group; }
        table tr { page-break-inside: avoid; }
        [class*="text-emerald"], [class*="text-amber"], [class*="text-sky"], [class*="text-white"], [class*="text-red"], .text-\\[var\\(--accent\\)\\] { color: #000 !important; }
      </style>
    </head><body>${sheet.outerHTML}</body></html>`;
    iframe.srcdoc = html;
    iframe.addEventListener("load", () => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          try {
            iframe.contentWindow.focus();
            iframe.contentWindow.print();
          } catch (e) {
            toast.error("Print failed — check pop-up permissions");
          }
          setTimeout(() => iframe.remove(), 1000);
        });
      });
    }, { once: true });
  };
  const openPreview = () => setPreviewOpen(true);
  const closePreview = () => setPreviewOpen(false);

  // Server-side PDF — passes `?customer=` so backend renders header with
  // full billing block + retitles the file.
  const pdfDownload = async () => {
    try {
      const params = customerName ? `?customer=${encodeURIComponent(customerName)}` : "";
      const res = await api.get(`/reports/pending-sales-order.pdf${params}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = customerName
        ? `Pending Order Statement - ${customerName}.pdf`
        : `pending-sales-order-${_todayIso()}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) { toast.error("PDF download failed"); }
  };

  const doExport = () => {
    if (!data) return;
    const suffix = customerName ? `-${customerName.replace(/[^a-z0-9]+/gi, "_")}` : "";
    const rows = tab === "article" ? filtered.article_rows : (tab === "eso" ? filtered.eso_lines : filtered.so_lines);
    const cols = tab === "article" ? [
      { key: "article_no", label: "Article / Product Code" },
      { key: "description", label: "Description" },
      { key: "pending_qty", label: "Pending Qty" },
      { key: "pending_value", label: "Pending Value (₹)" },
      { key: "order_count", label: "Order Lines" },
      { key: "customers", label: "Customers", get: r => (r.customers || []).join(" | ") },
      { key: "orders", label: "Orders", get: r => (r.orders || []).join(" | ") },
    ] : [
      { key: "source", label: "Src" },
      { key: "order_no", label: "Order No" },
      { key: "order_date", label: "Date" },
      { key: "delivery_date", label: "Delivery" },
      { key: "customer_name", label: "Customer" },
      { key: "po_number", label: "PO No" },
      { key: "style_no", label: "Style No" },
      { key: "article_no", label: "Article" },
      { key: "description", label: "Description" },
      { key: "colour", label: "Colour" },
      ...QTY_COLS.map(c => ({ key: c.k, label: c.label, get: r => (r[c.k] === null || r[c.k] === undefined ? "—" : r[c.k]) })),
      { key: "combined_cancel_close_qty", label: "Cancel+Close (combined)" },
      { key: "original_qty_tier", label: "Original Qty Evidence Tier", get: r => (r.original_qty === null || r.original_qty === undefined ? "UNKNOWN" : (r.original_qty_tier || "Recorded")) },
      { key: "readiness_block", label: "Protection Status", get: r => r.readiness_block || "" },
      { key: "rate", label: "Rate" },
      { key: "pending_value", label: "Pending Value" },
    ];
    exportToCSV(`pending_statement_${tab}${suffix}.csv`, rows, cols);
  };

  // PSOR-01 Q2 · all visible totals come from the filtered lines only.
  const totals = useMemo(() => ({
    so_line_count: filtered.so_lines.length,
    so_pending_qty: filtered.so_lines.reduce((a, r) => a + Number(r.pending_qty || 0), 0),
    so_pending_value: filtered.so_lines.reduce((a, r) => a + Number(r.pending_value || 0), 0),
    eso_line_count: filtered.eso_lines.length,
    eso_pending_qty: filtered.eso_lines.reduce((a, r) => a + Number(r.pending_qty || 0), 0),
    eso_pending_value: filtered.eso_lines.reduce((a, r) => a + Number(r.pending_value || 0), 0),
    article_count: filtered.article_rows.length,
  }), [filtered]);

  const filteredCustomerOpts = useMemo(() => {
    const s = customerSearch.trim().toLowerCase();
    return s ? customerOptions.filter(n => n.toLowerCase().includes(s)) : customerOptions;
  }, [customerOptions, customerSearch]);

  return (
    <div className="p-6 space-y-4" data-testid="pending-statement-page">
      {/* ─────────── Screen header (hidden in print) ─────────── */}
      <div className="flex items-end justify-between flex-wrap gap-3 print-hide">
        <div>
          <div className="overline mb-1">Reports</div>
          <h1 className="font-display text-3xl font-black tracking-tight">
            {customerName ? `Pending Order Statement · ${customerName}` : "Pending Sales Order"}
          </h1>
          <p className="text-xs text-zinc-500 mt-1">
            {customerName
              ? "Filtered view · every open SO + ESO line for the selected customer"
              : "SO + ESO — every open line with dispatched vs pending qty · product-code roll-up"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="ps-refresh" onClick={load} className="h-10 px-4 border border-[var(--border)] hover:bg-[var(--bg-2)] text-sm font-semibold rounded-sm flex items-center gap-2">
            <ArrowsClockwiseIcon size={13} weight="bold" />Refresh
          </button>
          <button data-testid="ps-export" onClick={doExport} className="h-10 px-4 border border-[var(--border)] hover:bg-[var(--bg-2)] text-sm font-semibold rounded-sm flex items-center gap-2">
            <DownloadSimpleIcon size={13} weight="bold" />Export CSV
          </button>
          <button data-testid="ps-pdf" onClick={pdfDownload} className="h-10 px-4 border border-red-500/40 text-red-300 hover:bg-red-500/10 text-sm font-semibold rounded-sm flex items-center gap-2">
            <FilePdfIcon size={13} weight="bold" />Download PDF
          </button>
          <button data-testid="ps-preview" onClick={openPreview} className="h-10 px-4 bg-[var(--accent)] hover:opacity-90 text-white text-sm font-semibold rounded-sm flex items-center gap-2">
            <PrinterIcon size={13} weight="bold" />Print Preview
          </button>
        </div>
      </div>

      {/* ─────────── In-App Print Preview Overlay (A4 landscape) ─────────── */}
      {previewOpen && (
        <PrintPreviewOverlay
          company={co}
          customer={selectedCustomer}
          generatedAt={data?.generated_at}
          soLines={filtered.so_lines}
          esoLines={filtered.eso_lines}
          articleRows={filtered.article_rows}
          productTotal={filtered.product_total}
          riskLabel={riskFilter === "overdue" ? "Overdue Only" : riskFilter === "due7" ? "Due in 7 Days" : riskFilter === "due30" ? "Due in 30 Days" : ""}
          onClose={closePreview}
          onPrint={doPrint}
        />
      )}

      {/* Customer Filter — searchable dropdown scoped to customers on open lines. */}
      <div className="tactical-card p-3 flex items-center gap-3 flex-wrap print-hide" data-testid="ps-customer-filter-bar">
        <div className="overline text-[10px]">Customer Filter</div>
        <div className="relative">
          <button
            data-testid="ps-customer-picker"
            onClick={() => setCustomerPickerOpen(v => !v)}
            className={`h-9 min-w-[300px] px-3 border rounded-sm text-sm font-semibold flex items-center justify-between gap-2 ${customerName ? "border-[var(--accent)] text-[var(--accent)] bg-[var(--accent)]/10" : "border-[var(--border)] text-zinc-300 hover:bg-[var(--bg-2)]"}`}
          >
            <span className="truncate">{customerName || "All Customers (Domestic + Export)"}</span>
            <CaretDownIcon size={12} weight="bold" />
          </button>
          {customerPickerOpen && (
            <div data-testid="ps-customer-dropdown" className="absolute z-30 mt-1 min-w-[340px] bg-[var(--bg-1)] border border-[var(--border)] rounded-sm shadow-xl">
              <div className="p-2 border-b border-[var(--border)]">
                <div className="relative">
                  <MagnifyingGlassIcon size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-500" />
                  <input
                    autoFocus
                    data-testid="ps-customer-search"
                    value={customerSearch}
                    onChange={e => setCustomerSearch(e.target.value)}
                    placeholder="Search customer…"
                    className="w-full h-8 pl-7 pr-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-xs focus:outline-none focus:border-[var(--accent)]"
                  />
                </div>
              </div>
              <div className="max-h-[300px] overflow-y-auto">
                <button
                  data-testid="ps-customer-opt-all"
                  onClick={() => { setCustomerName(""); setCustomerPickerOpen(false); setCustomerSearch(""); }}
                  className={`w-full text-left px-3 py-2 text-xs hover:bg-[var(--bg-2)] ${!customerName ? "text-[var(--accent)] font-semibold" : "text-zinc-300"}`}
                >
                  All Customers <span className="text-zinc-500 text-[10px]">({customerOptions.length})</span>
                </button>
                {filteredCustomerOpts.length === 0 && <div className="px-3 py-3 text-xs text-zinc-500 italic">No customers match.</div>}
                {filteredCustomerOpts.map(n => (
                  <button
                    key={n}
                    data-testid={`ps-customer-opt-${n}`}
                    onClick={() => { setCustomerName(n); setCustomerPickerOpen(false); setCustomerSearch(""); }}
                    className={`w-full text-left px-3 py-2 text-xs hover:bg-[var(--bg-2)] ${customerName === n ? "text-[var(--accent)] font-semibold" : "text-zinc-300"}`}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        {customerName && (
          <button data-testid="ps-customer-clear" onClick={() => setCustomerName("")}
            className="h-8 px-3 text-[10px] font-mono uppercase tracking-widest rounded-sm border border-[var(--border)] hover:bg-[var(--bg-2)] text-zinc-400 flex items-center gap-1">
            <XIcon size={11} weight="bold" />Clear
          </button>
        )}
        {customerName && selectedCustomer && (
          <div className="text-[10px] text-zinc-400 flex items-center gap-3 flex-wrap" data-testid="ps-customer-contact">
            {selectedCustomer.email && <span>✉ {selectedCustomer.email}</span>}
            {selectedCustomer.phone && <span>☎ {selectedCustomer.phone}</span>}
            {selectedCustomer.contact_person && <span>👤 {selectedCustomer.contact_person}</span>}
            {selectedCustomer.gstin && <span className="font-mono">GSTIN: {selectedCustomer.gstin}</span>}
          </div>
        )}
      </div>

      {/* Print-only sub-title showing which risk filter is active */}
      <div className="print-only hidden text-center border-b border-black pb-2 mb-3">
        <div className="font-black text-xl uppercase">{co.name}</div>
        <div className="text-[10px]">{co.address}</div>
        <div className="text-lg font-bold mt-2 uppercase">Pending Sales Order · SO + ESO{riskFilter === "overdue" ? " · OVERDUE ONLY" : riskFilter === "due7" ? " · DUE IN 7 DAYS" : riskFilter === "due30" ? " · DUE IN 30 DAYS" : ""}</div>
        <div className="text-[10px]">Generated: {new Date(data?.generated_at || Date.now()).toLocaleString("en-IN")}</div>
      </div>

      {/* ─────────── KPI band ─────────── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI label="SO Pending Lines" value={totals.so_line_count} tone="accent" testId="ps-kpi-so-count" />
        <KPI label="SO Pending Qty" value={`${fmtQty(totals.so_pending_qty)} m`} tone="accent" testId="ps-kpi-so-qty" />
        <KPI label="ESO Pending Qty" value={`${fmtQty(totals.eso_pending_qty)} m`} tone="emerald" testId="ps-kpi-eso-qty" />
        <KPI label="Products in Backlog" value={totals.article_count} tone="amber" testId="ps-kpi-articles" />
      </div>

      {/* ─────────── Filter + Tabs (screen only) ─────────── */}
      <div className="print-hide flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-1">
          {[
            { k: "so", label: "SO · Line-wise", n: filtered.so_lines.length },
            { k: "eso", label: "ESO · Line-wise", n: filtered.eso_lines.length },
            { k: "article", label: "Product Code · Aggregate", n: filtered.article_rows.length },
          ].map(t => (
            <button key={t.k} data-testid={`ps-tab-${t.k}`} onClick={() => setTab(t.k)}
              className={`h-9 px-4 text-xs font-semibold rounded-sm border ${tab === t.k ? "bg-[var(--accent)] border-[var(--accent)] text-white" : "border-[var(--border)] text-zinc-400 hover:text-white"}`}>
              {t.label} <span className="text-[10px] opacity-70">({t.n})</span>
            </button>
          ))}
        </div>
      </div>

      {/* Feb 2026 · Dedicated search bar — full-width, prominent, matches
          every column value across all three sections (order no, article
          / product code, customer, PO no, colour, size, description).
          Users asked for this to be its own row so long queries stay
          readable and never get elbowed by the tab strip. */}
      <div className="print-hide">
        <div className="relative">
          <MagnifyingGlassIcon size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--accent)]" />
          <input
            data-testid="ps-search"
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Search by Order No · Article / Product Code · Customer · PO No · Colour · Description…"
            className="w-full h-11 pl-11 pr-24 bg-[var(--bg-1)] border-2 border-[var(--border)] rounded-sm text-sm focus:outline-none focus:border-[var(--accent)] placeholder:text-zinc-500"
          />
          {q && (
            <button
              data-testid="ps-search-clear"
              onClick={() => setQ("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 h-7 px-3 text-[10px] rounded-full border border-[var(--border)] hover:border-[var(--accent)] text-zinc-400 hover:text-white font-mono uppercase tracking-widest"
            >
              Clear ✕
            </button>
          )}
        </div>
        {q && (
          <div data-testid="ps-search-summary" className="mt-1 text-[10px] uppercase tracking-widest text-[var(--accent)] font-mono">
            · Filter "{q}" matched {filtered.so_lines.length} SO · {filtered.eso_lines.length} ESO · {filtered.article_rows.length} products
          </div>
        )}
      </div>

      {/* Feb 2026 · At-risk delivery filter chips — hidden in print but the
          filter itself stays applied so leadership prints exactly what they
          see (e.g. "Overdue only" → 3-page overdue statement). */}
      <div className="print-hide flex items-center gap-1 flex-wrap">
        <span className="overline text-[10px] mr-1">Delivery Risk</span>
        {[
          { k: "all",      label: "All",              tone: "zinc"    },
          { k: "overdue",  label: "Overdue Only",     tone: "red"     },
          { k: "due7",     label: "Due in 7 Days",    tone: "amber"   },
          { k: "due30",    label: "Due in 30 Days",   tone: "emerald" },
        ].map(f => {
          const active = riskFilter === f.k;
          const activeCls = {
            zinc:    "bg-zinc-500/20 border-zinc-400 text-white",
            red:     "bg-red-500/20 border-red-400 text-red-200",
            amber:   "bg-amber-500/20 border-amber-400 text-amber-200",
            emerald: "bg-emerald-500/20 border-emerald-400 text-emerald-200",
          }[f.tone];
          return (
            <button
              key={f.k}
              data-testid={`ps-risk-${f.k}`}
              onClick={() => setRiskFilter(f.k)}
              className={`h-7 px-3 text-[11px] rounded-full border font-mono uppercase tracking-widest transition ${active ? activeCls : "border-[var(--border)] text-zinc-400 hover:text-white"}`}
            >
              {f.label}
            </button>
          );
        })}
        {riskFilter !== "all" && (
          <span data-testid="ps-risk-active-badge" className="ml-2 text-[10px] uppercase tracking-widest text-amber-300 font-mono">
            · showing at-risk lines only
          </span>
        )}
      </div>

      {/* ─────────── Content sections ─────────── */}
      {loading && <div className="tactical-card p-10 text-center text-zinc-500 text-sm">Loading pending statement…</div>}

      {!loading && (
        <>
          {/* SO line-wise · shown for tab=so AND in print (all sections) */}
          <section className={`tactical-card p-4 ${tab === "so" ? "" : "print-only hidden"}`}>
            <div className="overline mb-2 flex items-center gap-2">
              <StackIcon size={13} weight="bold" /> SO · Line-wise Pending ({filtered.so_lines.length} lines)
            </div>
            <LineTable rows={filtered.so_lines} testPrefix="ps-so" />
          </section>

          <section className={`tactical-card p-4 ${tab === "eso" ? "" : "print-only hidden"}`}>
            <div className="overline mb-2 flex items-center gap-2">
              <StackIcon size={13} weight="bold" /> ESO · Line-wise Pending ({filtered.eso_lines.length} lines)
            </div>
            <LineTable rows={filtered.eso_lines} testPrefix="ps-eso" />
          </section>

          <section className={`tactical-card p-4 ${tab === "article" ? "" : "print-only hidden"}`}>
            <div className="overline mb-2 flex items-center gap-2">
              <StackIcon size={13} weight="bold" /> Product Code / Article-wise Aggregate ({filtered.article_rows.length} products)
            </div>
            <div className="border border-[var(--border)] rounded-sm overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[var(--bg-2)]/50 text-zinc-500 uppercase tracking-widest text-[10px]">
                  <tr>
                    <th className="px-3 py-2 text-left">Article / Product Code</th>
                    <th className="px-3 py-2 text-left">Description</th>
                    <th className="px-3 py-2 text-left">Earliest Delivery</th>
                    <th className="px-3 py-2 text-right">Pending Qty</th>
                    <th className="px-3 py-2 text-right">Pending Value (₹)</th>
                    <th className="px-3 py-2 text-center">Order Lines</th>
                    <th className="px-3 py-2 text-left">Customers</th>
                    <th className="px-3 py-2 text-left print-hide">Orders</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.article_rows.length === 0 && (
                    <tr><td colSpan={8} className="px-3 py-8 text-center text-zinc-500 text-xs">No pending items 🎉</td></tr>
                  )}
                  {filtered.article_rows.length > 0 && (
                    <tr data-testid="ps-product-sum-total" className="border-t-2 border-b-2 border-[var(--accent)] bg-[var(--bg-2)]/60">
                      <td colSpan={3} className="px-3 py-2 uppercase text-[10px] tracking-widest text-zinc-300 font-semibold">Product-wise Sum Total</td>
                      <td data-testid="ps-product-sum-qty" className="px-3 py-2 text-right font-mono tabular-nums font-bold text-white">{fmtQty(filtered.product_total.pending_qty)}</td>
                      <td data-testid="ps-product-sum-value" className="px-3 py-2 text-right font-mono tabular-nums font-bold text-emerald-300">₹{fmt(filtered.product_total.pending_value)}</td>
                      <td data-testid="ps-product-sum-lines" className="px-3 py-2 text-center font-mono tabular-nums font-bold text-zinc-300">{filtered.product_total.order_lines}</td>
                      <td colSpan={2}></td>
                    </tr>
                  )}
                  {filtered.article_rows.map((r, i) => {
                    const ds = deliveryStatus(r.earliest_delivery_date);
                    return (
                    <tr key={r.product_group_key + i} data-testid={`ps-art-${i}`} className="border-t border-[var(--border)] hover:bg-[var(--bg-2)]/30">
                      <td className="px-3 py-2 font-mono text-[var(--accent)] text-xs whitespace-nowrap">{r.article_no}{r.product_conflict && <ConflictFlag />}</td>
                      <td className="px-3 py-2 text-xs text-zinc-400">{r.description || "—"}</td>
                      <td className="px-3 py-2 text-xs">
                        <div className="text-zinc-300 font-mono">{r.earliest_delivery_date || "—"}</div>
                        <div className={`text-[10px] uppercase tracking-widest ${ds.color}`}>{ds.label}</div>
                      </td>
                      <td className="px-3 py-2 text-right font-mono tabular-nums font-semibold text-amber-300">{fmtQty(r.pending_qty)}</td>
                      <td className="px-3 py-2 text-right font-mono tabular-nums text-emerald-300">₹{fmt(r.pending_value)}</td>
                      <td className="px-3 py-2 text-center font-mono tabular-nums text-zinc-400">{r.order_count}</td>
                      <td className="px-3 py-2 text-xs text-zinc-400">{(r.customers || []).join(", ") || "—"}</td>
                      <td className="px-3 py-2 text-[10px] text-zinc-500 print-hide">{(r.orders || []).join(", ") || "—"}</td>
                    </tr>
                  );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </div>
  );
}

function KPI({ label, value, tone = "accent", testId }) {
  const color = { accent: "text-[var(--accent)]", emerald: "text-emerald-400", amber: "text-amber-400" }[tone];
  return (
    <div className="rounded-xl border border-[var(--border)] bg-[var(--bg-1)] p-4" data-testid={testId}>
      <div className="overline text-[10px]">{label}</div>
      <div className={`font-display font-black text-2xl mt-1 tabular-nums ${color}`}>{value ?? 0}</div>
    </div>
  );
}

/**
 * Full-screen A4-landscape print preview overlay. Renders on a light-paper
 * background so the user sees exactly what the printer will emit. All three
 * sections stack vertically inside a fixed 297×210mm frame; the toolbar at
 * the top has real Print + Close actions (Print reuses `window.print()`
 * which then honours the same print CSS defined in App.css).
 */
function PrintPreviewOverlay({ company, customer, generatedAt, soLines, esoLines, articleRows, productTotal, riskLabel, onClose, onPrint }) {
  const title = customer ? `Pending Order Statement · ${customer.name}` : "Pending Sales Order";
  return (
    /* Feb 2026 · Overlay stays in the DOM during print (no `print-hide`) so
       the A4-landscape PrintSheet inside can be the ONLY thing visible when
       the browser hits @media print. Backdrop + toolbar carry `print-hide`
       so they collapse. Visibility trick in App.css guarantees the sheet
       actually reaches the paper even with landscape orientation. */
    <div className="fixed inset-0 z-[10000] flex flex-col" data-testid="ps-preview-overlay">
      <div className="absolute inset-0 bg-black/80 print-hide" />
      {/* Top toolbar */}
      <div className="relative flex items-center justify-between px-6 py-3 bg-[var(--bg-1)] border-b border-[var(--border)] print-hide">
        <div>
          <div className="overline text-[10px]">Print Preview · A4 Landscape{riskLabel ? ` · ${riskLabel}` : ""}</div>
          <div className="font-display text-sm font-bold text-white">{title}</div>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="ps-preview-print" onClick={onPrint} className="h-9 px-4 bg-[var(--accent)] hover:opacity-90 text-white text-sm font-semibold rounded-sm flex items-center gap-2">
            <PrinterIcon size={13} weight="bold" />Print
          </button>
          <button data-testid="ps-preview-close" onClick={onClose} className="h-9 px-3 border border-[var(--border)] hover:bg-[var(--bg-2)] text-sm font-semibold rounded-sm flex items-center gap-2">
            <XIcon size={13} weight="bold" />Close
          </button>
        </div>
      </div>
      {/* Preview canvas · scrollable container so multi-page reports don't clip */}
      <div className="relative flex-1 overflow-auto p-6 bg-zinc-800">
        <PrintSheet
          company={company}
          customer={customer}
          generatedAt={generatedAt}
          soLines={soLines}
          esoLines={esoLines}
          articleRows={articleRows}
          productTotal={productTotal}
          riskLabel={riskLabel}
        />
      </div>
    </div>
  );
}

// A4 landscape at 96 DPI ≈ 1123 × 794 px. Fixed width so the preview mirrors
// the paper aspect exactly; height grows with content and CSS `page-break`
// hints inside sections keep tables intact.
function PrintSheet({ company, customer, generatedAt, soLines, esoLines, articleRows, productTotal, riskLabel }) {
  const soTotals = sumQtyCols(soLines);
  const esoTotals = sumQtyCols(esoLines);
  return (
    <div id="ps-print-sheet" className="mx-auto bg-white text-black shadow-2xl" style={{ width: "1123px", minHeight: "794px", padding: "24px 32px" }}>
      {/* Letterhead */}
      <div className="text-center border-b-2 border-black pb-2 mb-3">
        <div className="font-black text-xl uppercase tracking-wide">{company.name}</div>
        <div className="text-[10px] whitespace-pre-line">{company.address}</div>
        <div className="text-lg font-bold mt-2 uppercase">
          {customer ? `Pending Order Statement · ${customer.name}` : "Pending Sales Order · SO + ESO"}
        </div>
        <div className="text-[10px]">Generated: {new Date(generatedAt || Date.now()).toLocaleString("en-IN")}</div>
      </div>

      {/* Customer billing block — email-ready when filtered */}
      {customer && (
        <div className="flex justify-between items-start border border-black rounded-sm p-2 mb-3 text-[10px]">
          <div>
            <div className="text-[8px] uppercase tracking-widest text-gray-600">Bill To</div>
            <div className="font-bold text-[12px]">{customer.name}</div>
            {customer.contact_person && <div>Attn: {customer.contact_person}</div>}
            {customer.address && <div className="whitespace-pre-line">{customer.address}</div>}
            {(customer.city || customer.state || customer.pincode || customer.country) && (
              <div>{[customer.city, customer.state, customer.pincode, customer.country].filter(Boolean).join(", ")}</div>
            )}
          </div>
          <div className="text-right">
            {customer.email && <div>✉ {customer.email}</div>}
            {customer.phone && <div>☎ {customer.phone}</div>}
            {customer.gstin && <div className="font-mono">GSTIN: {customer.gstin}</div>}
          </div>
        </div>
      )}

      {/* Section 1 · SO line-wise */}
      {soLines.length > 0 && <PrintSectionLines title={`SO · Line-wise Pending (${soLines.length} lines)`} rows={soLines} totals={soTotals} />}
      {/* Section 2 · ESO line-wise */}
      {esoLines.length > 0 && <PrintSectionLines title={`ESO · Line-wise Pending (${esoLines.length} lines)`} rows={esoLines} totals={esoTotals} />}
      {/* Section 3 · Article aggregate */}
      <div className="mt-4" data-testid="ps-print-product-section">
        <div className="text-[11px] font-bold uppercase tracking-widest mb-1 border-b border-black pb-0.5" style={{ pageBreakAfter: "avoid" }}>
          Product Code / Article-wise Aggregate ({articleRows.length} products)
        </div>
        <table className="w-full text-[10px] border-collapse">
          <thead>
            <tr className="bg-gray-200">
              <th className="border border-gray-400 px-2 py-1 text-left">Article / Product Code</th>
              <th className="border border-gray-400 px-2 py-1 text-left">Description</th>
              <th className="border border-gray-400 px-2 py-1 text-right">Pending Qty</th>
              <th className="border border-gray-400 px-2 py-1 text-right">Pending Value (₹)</th>
              <th className="border border-gray-400 px-2 py-1 text-center">Order Lines</th>
              <th className="border border-gray-400 px-2 py-1 text-left">Customers</th>
            </tr>
          </thead>
          <tbody>
            {articleRows.length === 0 && (
              <tr><td colSpan={6} className="border border-gray-400 px-2 py-3 text-center italic">No pending items.</td></tr>
            )}
            {articleRows.length > 0 && (
              <tr data-testid="ps-print-product-sum-total" className="bg-gray-300 font-bold border-y-2 border-black">
                <td className="border border-gray-400 px-2 py-1 uppercase" colSpan={2}>Product-wise Sum Total</td>
                <td className="border border-gray-400 px-2 py-1 text-right font-mono tabular-nums">{fmtQty(productTotal.pending_qty)}</td>
                <td className="border border-gray-400 px-2 py-1 text-right font-mono tabular-nums">₹{fmt(productTotal.pending_value)}</td>
                <td className="border border-gray-400 px-2 py-1 text-center font-mono">{productTotal.order_lines}</td>
                <td className="border border-gray-400 px-2 py-1"></td>
              </tr>
            )}
            {articleRows.map((r, i) => (
              <tr key={i} style={{ pageBreakInside: "avoid" }}>
                <td className="border border-gray-400 px-2 py-1 font-mono" style={{ wordBreak: "break-word" }}>{r.article_no}{r.product_conflict && <ConflictFlag print />}</td>
                <td className="border border-gray-400 px-2 py-1">{r.description || "—"}</td>
                <td className="border border-gray-400 px-2 py-1 text-right font-mono tabular-nums font-bold">{fmtQty(r.pending_qty)}</td>
                <td className="border border-gray-400 px-2 py-1 text-right font-mono tabular-nums">₹{fmt(r.pending_value)}</td>
                <td className="border border-gray-400 px-2 py-1 text-center font-mono">{r.order_count}</td>
                <td className="border border-gray-400 px-2 py-1">{(r.customers || []).join(", ") || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-6 pt-2 border-t border-black text-[9px] text-gray-700 flex justify-between">
        <span>Confidential · For internal use only</span>
        <span>Page 1 · Landscape · A4</span>
      </div>
    </div>
  );
}

function PrintSectionLines({ title, rows, totals }) {
  const mark = (k) => (totals.partialCols.has(k) ? " †" : "") + (k === "original_qty" && hasTierC(rows) ? " ‡" : "");
  return (
    <div className="mt-4">
      <div className="text-[11px] font-bold uppercase tracking-widest mb-1 border-b border-black pb-0.5" style={{ pageBreakAfter: "avoid" }}>{title}</div>
      <table className="w-full text-[9px] border-collapse" style={{ tableLayout: "fixed" }}>
        <colgroup>
          <col style={{ width: "7%" }} />
          <col style={{ width: "6.5%" }} />
          <col style={{ width: "10%" }} />
          <col style={{ width: "6%" }} />
          <col style={{ width: "19.5%" }} />
          {QTY_COLS.map(c => <col key={c.k} style={{ width: "7.29%" }} />)}
        </colgroup>
        <thead>
          <tr className="bg-gray-200">
            <th className="border border-gray-400 px-1.5 py-1 text-left">Order</th>
            <th className="border border-gray-400 px-1.5 py-1 text-left">Delivery</th>
            <th className="border border-gray-400 px-1.5 py-1 text-left">Customer</th>
            <th className="border border-gray-400 px-1.5 py-1 text-left">PO No</th>
            <th className="border border-gray-400 px-1.5 py-1 text-left">Article / Product Code</th>
            {QTY_COLS.map(c => <th key={c.k} className="border border-gray-400 px-1 py-1 text-right">{c.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr><td colSpan={12} className="border border-gray-400 px-2 py-3 text-center italic">No pending lines.</td></tr>
          )}
          {rows.length > 0 && (
            <tr className="bg-gray-300 font-bold border-y-2 border-black">
              <td className="border border-gray-400 px-1.5 py-1 uppercase" colSpan={5}>Sum Total</td>
              {QTY_COLS.map(c => (
                <td key={c.k} className="border border-gray-400 px-1 py-1 text-right font-mono tabular-nums">
                  {fmtQty(totals[c.k])}{mark(c.k)}
                </td>
              ))}
            </tr>
          )}
          {rows.map((r, i) => {
            const ds = deliveryStatus(r.delivery_date);
            return (
            <tr key={i} style={{ pageBreakInside: "avoid" }}>
              <td className="border border-gray-400 px-1.5 py-1 text-right"><TwoLineDocNo value={r.order_no} className="text-gray-900" /></td>
              <td className="border border-gray-400 px-1.5 py-1">
                <div className="font-mono">{(r.delivery_date || "—").slice(0, 10)}</div>
                <div className={`text-[8px] uppercase ${ds.print}`}>{ds.label}</div>
              </td>
              <td className="border border-gray-400 px-1.5 py-1" style={{ wordBreak: "break-word" }}>{r.customer_name || "—"}</td>
              <td className="border border-gray-400 px-1.5 py-1" style={{ wordBreak: "break-word" }}>{r.po_number || "—"}</td>
              <td className="border border-gray-400 px-1.5 py-1 font-mono" style={{ wordBreak: "break-word" }}>{r.article_no}<ProtectionFlag r={r} print /><div className="text-[8px] text-gray-600">{r.description}</div></td>
              {QTY_COLS.map(c => (
                <td key={c.k} className={`border border-gray-400 px-1 py-1 text-right font-mono tabular-nums ${c.k === "pending_qty" ? "font-bold" : ""}`}>
                  {c.k === "cancel_qty" ? <CancelCell r={r} print /> : fmtOpt(r[c.k]) + tierMark(r, c.k)}
                </td>
              ))}
            </tr>
          );
          })}
        </tbody>
      </table>
      {totals.partial && <div className="text-[8px] text-gray-700 mt-0.5">{PARTIAL_NOTE}</div>}
      {hasTierC(rows) && <div className="text-[8px] text-gray-700 mt-0.5">{TIER_C_NOTE}</div>}
    </div>
  );
}

function LineTable({ rows, testPrefix }) {
  const total = sumQtyCols(rows);
  const mark = (k) => (total.partialCols.has(k) ? " †" : "") + (k === "original_qty" && hasTierC(rows) ? " ‡" : "");
  return (
    <div className="border border-[var(--border)] rounded-sm overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-[var(--bg-2)]/50 text-zinc-500 uppercase tracking-widest text-[10px]">
          <tr>
            <th className="px-2 py-2 text-left">Order</th>
            <th className="px-2 py-2 text-left">Date</th>
            <th className="px-2 py-2 text-left">Delivery</th>
            <th className="px-2 py-2 text-left">Customer</th>
            <th className="px-2 py-2 text-left">PO No</th>
            <th className="px-2 py-2 text-left">Style No</th>
            <th className="px-2 py-2 text-left">Article / Product Code</th>
            <th className="px-2 py-2 text-left print-hide">Colour</th>
            {QTY_COLS.map(c => (
              <th key={c.k} className="px-2 py-2 text-right" title={c.k === "pending_qty" ? "Pending = Effective − Cancel − Close − Delivered (production buffer excluded)" : undefined}>{c.label}</th>
            ))}
            <th className="px-2 py-2 text-right">Rate ₹</th>
            <th className="px-2 py-2 text-right">Pending Value ₹</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr><td colSpan={17} className="px-3 py-8 text-center text-zinc-500 text-xs">No pending lines 🎉</td></tr>
          )}
          {rows.length > 0 && (
            <tr data-testid={`${testPrefix}-total-row`} className="border-t-2 border-b-2 border-[var(--accent)] bg-[var(--bg-2)]/60 sticky top-0">
              <td colSpan={8} className="px-2 py-2 uppercase text-[10px] tracking-widest text-zinc-300 font-semibold">Total</td>
              {QTY_COLS.map(c => (
                <td key={c.k} data-testid={`${testPrefix}-total-${c.k}`} className={`px-2 py-2 text-right font-mono tabular-nums font-bold ${c.k === "pending_qty" ? "text-amber-300" : c.k === "disp_qty" ? "text-emerald-300" : "text-white"}`}>
                  {fmtQty(total[c.k])}{mark(c.k)}
                </td>
              ))}
              <td></td>
              <td data-testid={`${testPrefix}-total-value`} className="px-2 py-2 text-right font-mono tabular-nums font-bold text-emerald-300">₹{fmt(total.value)}</td>
            </tr>
          )}
          {rows.map((r, i) => {
            const ds = deliveryStatus(r.delivery_date);
            return (
            <tr key={i} data-testid={`${testPrefix}-row-${i}`} className="border-t border-[var(--border)] hover:bg-[var(--bg-2)]/30">
              <td className="px-2 py-1.5 text-right whitespace-nowrap"><TwoLineDocNo value={r.order_no} className="text-[var(--accent)] text-xs" /></td>
              <td className="px-2 py-1.5 text-xs text-zinc-400">{(r.order_date || "").slice(0, 10)}</td>
              <td className="px-2 py-1.5 text-xs">
                <div className="text-zinc-300 font-mono">{(r.delivery_date || "—").slice(0, 10)}</div>
                <div className={`text-[9px] uppercase tracking-widest ${ds.color}`}>{ds.label}</div>
              </td>
              <td className="px-2 py-1.5 text-xs">{r.customer_name || "—"}</td>
              <td className="px-2 py-1.5 text-xs text-zinc-400">{r.po_number || "—"}</td>
              <td className="px-2 py-1.5 text-xs font-mono text-zinc-300">{r.style_no || "—"}</td>
              <td className="px-2 py-1.5 font-mono text-xs">{r.article_no}<ProtectionFlag r={r} /><div className="text-[10px] text-zinc-500">{r.description}</div></td>
              <td className="px-2 py-1.5 text-xs text-zinc-400 print-hide">{r.colour || "—"}</td>
              {QTY_COLS.map(c => (
                <td key={c.k} data-testid={`${testPrefix}-row-${i}-${c.k}`} className={`px-2 py-1.5 text-right font-mono tabular-nums text-xs ${c.k === "pending_qty" ? "font-semibold text-amber-300" : c.k === "disp_qty" ? "text-emerald-300" : c.k === "effective_qty" ? "font-semibold" : "text-zinc-400"}`}>
                  {c.k === "cancel_qty" ? <CancelCell r={r} /> : fmtOpt(r[c.k]) + tierMark(r, c.k)}
                </td>
              ))}
              <td className="px-2 py-1.5 text-right font-mono tabular-nums text-xs text-zinc-400">{fmt(r.rate)}</td>
              <td className="px-2 py-1.5 text-right font-mono tabular-nums text-xs text-emerald-300">{fmt(r.pending_value)}</td>
            </tr>
          );
          })}
        </tbody>
      </table>
      {total.partial && <div data-testid={`${testPrefix}-partial-note`} className="px-3 py-1.5 text-[10px] text-zinc-500 border-t border-[var(--border)]">{PARTIAL_NOTE}</div>}
      {hasTierC(rows) && <div data-testid={`${testPrefix}-tierc-note`} className="px-3 py-1.5 text-[10px] text-zinc-500 border-t border-[var(--border)]">{TIER_C_NOTE}</div>}
    </div>
  );
}
